import { PropAsset } from './PropAsset';

// Fence - Çit / Alçak Duvar
// Low fence or wall that provides partial obstruction
export class Fence extends PropAsset {
  readonly id = 'fence';
  readonly width = 64;
  readonly height = 40;
  readonly walkable = false;
  readonly collisionRadius = 0.7;
  readonly occludesPlayer = false;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    // Fence styles
    const fenceColors = [
      { main: '#8b7355', dark: '#5a4a3a', light: '#a08570', type: 'wood' },   // Wooden
      { main: '#6b6b6b', dark: '#4a4a4a', light: '#8a8a8a', type: 'stone' },  // Stone
      { main: '#4a3a2a', dark: '#2a1a0a', light: '#6a5a4a', type: 'iron' },   // Iron
    ];
    const colors = fenceColors[v % fenceColors.length];

    const fenceWidth = 50 * s;
    const fenceHeight = 25 * s;
    const postWidth = 6 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 2 * s, 25 * s, 8 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    if (colors.type === 'wood') {
      // Wooden fence - horizontal planks

      // Left post
      ctx.fillStyle = colors.dark;
      ctx.fillRect(cx - fenceWidth / 2, cy - fenceHeight, postWidth, fenceHeight);

      // Right post
      ctx.fillRect(cx + fenceWidth / 2 - postWidth, cy - fenceHeight, postWidth, fenceHeight);

      // Horizontal planks
      ctx.fillStyle = colors.main;
      const plankH = 6 * s;
      for (let i = 0; i < 3; i++) {
        const py = cy - fenceHeight + 5 * s + i * (plankH + 3 * s);
        ctx.fillRect(cx - fenceWidth / 2, py, fenceWidth, plankH);

        // Plank highlight
        ctx.fillStyle = colors.light;
        ctx.fillRect(cx - fenceWidth / 2, py, fenceWidth, 1.5 * s);
        ctx.fillStyle = colors.main;
      }

      // Post tops (rounded)
      ctx.fillStyle = colors.light;
      ctx.beginPath();
      ctx.arc(cx - fenceWidth / 2 + postWidth / 2, cy - fenceHeight, postWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx + fenceWidth / 2 - postWidth / 2, cy - fenceHeight, postWidth / 2, 0, Math.PI * 2);
      ctx.fill();

    } else if (colors.type === 'stone') {
      // Stone low wall
      ctx.fillStyle = colors.dark;
      ctx.beginPath();
      ctx.moveTo(cx - fenceWidth / 2, cy);
      ctx.lineTo(cx - fenceWidth / 2, cy - fenceHeight);
      ctx.lineTo(cx, cy - fenceHeight - 5 * s);
      ctx.lineTo(cx, cy - 5 * s);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = colors.main;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 5 * s);
      ctx.lineTo(cx, cy - fenceHeight - 5 * s);
      ctx.lineTo(cx + fenceWidth / 2, cy - fenceHeight);
      ctx.lineTo(cx + fenceWidth / 2, cy);
      ctx.closePath();
      ctx.fill();

      // Top
      ctx.fillStyle = colors.light;
      ctx.beginPath();
      ctx.moveTo(cx - fenceWidth / 2, cy - fenceHeight);
      ctx.lineTo(cx, cy - fenceHeight - 5 * s);
      ctx.lineTo(cx + fenceWidth / 2, cy - fenceHeight);
      ctx.lineTo(cx, cy - fenceHeight + 3 * s);
      ctx.closePath();
      ctx.fill();

      // Stone lines
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1 * s;
      ctx.beginPath();
      ctx.moveTo(cx, cy - fenceHeight / 2 - 5 * s);
      ctx.lineTo(cx + fenceWidth / 2, cy - fenceHeight / 2);
      ctx.stroke();

    } else {
      // Iron fence - vertical bars
      ctx.fillStyle = colors.main;

      // Top rail
      ctx.fillRect(cx - fenceWidth / 2, cy - fenceHeight, fenceWidth, 4 * s);
      // Bottom rail
      ctx.fillRect(cx - fenceWidth / 2, cy - 8 * s, fenceWidth, 4 * s);

      // Vertical bars
      const barCount = 7;
      const barSpacing = fenceWidth / (barCount + 1);
      ctx.fillStyle = colors.dark;
      for (let i = 1; i <= barCount; i++) {
        const bx = cx - fenceWidth / 2 + i * barSpacing;
        ctx.fillRect(bx - 1.5 * s, cy - fenceHeight, 3 * s, fenceHeight);

        // Pointed top
        ctx.beginPath();
        ctx.moveTo(bx - 2 * s, cy - fenceHeight);
        ctx.lineTo(bx, cy - fenceHeight - 4 * s);
        ctx.lineTo(bx + 2 * s, cy - fenceHeight);
        ctx.closePath();
        ctx.fill();
      }
    }
  }
}

// Fence Corner - Çit Köşesi
export class FenceCorner extends PropAsset {
  readonly id = 'fence_corner';
  readonly width = 48;
  readonly height = 40;
  readonly walkable = false;
  readonly collisionRadius = 0.6;
  readonly occludesPlayer = false;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const fenceColors = [
      { main: '#8b7355', dark: '#5a4a3a', light: '#a08570' },
      { main: '#6b6b6b', dark: '#4a4a4a', light: '#8a8a8a' },
    ];
    const colors = fenceColors[v % fenceColors.length];

    const size = 20 * s;
    const height = 25 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 2 * s, 15 * s, 8 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Corner post
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(cx - size / 2, cy);
    ctx.lineTo(cx - size / 2, cy - height);
    ctx.lineTo(cx, cy - height);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = colors.main;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx, cy - height);
    ctx.lineTo(cx + size / 2, cy - height);
    ctx.lineTo(cx + size / 2, cy);
    ctx.closePath();
    ctx.fill();

    // Top
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - size / 2, cy - height);
    ctx.lineTo(cx, cy - height - size / 4);
    ctx.lineTo(cx + size / 2, cy - height);
    ctx.lineTo(cx, cy - height + size / 4);
    ctx.closePath();
    ctx.fill();
  }
}
